import type { UIElement } from '../../components/ui-element';
import type { ComponentPlugin } from '../../components/ui-element.type';

/**
 * @class
 * @memberof SharedComponents
 * @classdesc Class for lazy-loading marketing plugin.
 * @alias LazyLoadingPlugin
 * @param {UIElement} target
 * @implements {ComponentPlugin}
 */
export class LazyLoadingPlugin implements ComponentPlugin {
    private readonly target: UIElement;
    private observer!: IntersectionObserver;

    constructor(target: UIElement) {
        this.target = target;
    }

    /**
     * Intersection threshold, 0 - when the first pixel is intersected, 1 - when all pixels,
     * are intersected.
     * @type {number}
     */
    static readonly THRESHOLD = 0;

    /**
     * Intersection handler.
     * @param {Array<IntersectionObserverEntry>} entries
     * @param {IntersectionObserver} observer
     * @private
     */
    private intersectionHandler(
        entries: Array<IntersectionObserverEntry>,
        observer: IntersectionObserver
    ): void {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const images =
                    entry.target.querySelectorAll<HTMLImageElement>(
                        'img[data-src]'
                    );
                images.forEach((img) => {
                    img.src = img.dataset.src!;
                    delete img.dataset.src;
                    // When image is loaded we don't need observer anymore.
                    observer.disconnect();
                });
            }
        });
    }

    /**
     * @inheritDoc
     */
    render(): void {
        const images =
            this.target.querySelectorAll<HTMLImageElement>('img[src]');
        images.forEach((img) => {
            if (img.src) {
                img.dataset.src = img.src;
                img.removeAttribute('src');
            }
        });
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        const params: IntersectionObserverInit = {
            rootMargin: '0px',
            threshold: LazyLoadingPlugin.THRESHOLD,
        };
        this.observer = new IntersectionObserver(
            (entries, observer) => this.intersectionHandler(entries, observer),
            params
        );
        this.observer.observe(this.target);
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.observer.disconnect();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.observer.observe(this.target);
    }
}
